/**
 * Handles syncing editor content with stores and preserving special content
 */
export class EditorContentService {
    constructor(specStore, regenerativeContentStore) {
        this.specStore = specStore;
        this.regenerativeContentStore = regenerativeContentStore;
        this.isUpdatingFromStore = false;

        // Subscribe to regenerative content store to get latest content
        this.unsubscribe = this.regenerativeContentStore.subscribe(content => {
            this.regenerativeContent = content;
        });
    }

    /**
     * Update content in the store, preserving special content like regenerative blocks
     */
    updateContent(content) {
        if (this.isUpdatingFromStore) return;

        // Handle undefined or null content
        if (!content) {
            this.specStore.update(state => ({
                ...state,
                content: ''
            }));
            return;
        }

        // For regenerative blocks, ensure newlines are preserved
        const preservedContent = content.replace(
            /(<p[^>]*regenerative-support[^>]*>)(.*?)(<\/p>)/gs,
            (match, openTag, content, closeTag) => {
                return openTag + content.replace(/\n/g, '<br>') + closeTag;
            }
        );

        this.specStore.update(state => ({
            ...state,
            content: preservedContent
        }));
    }

    /**
     * Update editor content from store
     */
    updateFromStore(editor) {
        this.isUpdatingFromStore = true;
        // Get the current value from the store
        let currentContent;
        const unsubscribe = this.specStore.subscribe(value => {
            currentContent = value.content;
        });
        unsubscribe();
        
        if (currentContent !== undefined) {
            editor.commands.setContent(currentContent);
        }
        this.isUpdatingFromStore = false;
    }

    /**
     * Update editor content from regenerative content store
     */
    updateFromRegenerativeStore(editor) {
        if (!this.regenerativeContent || !editor) return;
        
        
        // Parse the new content
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(this.regenerativeContent, 'text/html');
        
        // Get all regenerative blocks from the new content
        const newRegenerativeBlocks = Array.from(
            newDoc.querySelectorAll('p[data-block-type="regenerative-support"]')
        );
        
        
        if (newRegenerativeBlocks.length === 0) {
            // If no regenerative blocks in new content, use setContent as fallback
            editor.commands.setContent(this.regenerativeContent);
            return;
        }
        
        // Group new blocks by context
        const newBlocksByContext = {};
        newRegenerativeBlocks.forEach(block => {
            const context = block.getAttribute('data-context');
            if (!newBlocksByContext[context]) {
                newBlocksByContext[context] = [];
            }
            newBlocksByContext[context].push(block);
        });
        
        // Use a transaction to update all regenerative blocks at once
        const { state, view } = editor;
        const { tr } = state;
        
        // Find and update existing regenerative blocks in the editor
        let hasUpdates = false;
        const processedContexts = new Set();
        
        state.doc.descendants((node, _pos) => {
            if (node.type.name === 'paragraph' && 
                node.attrs.blockType === 'regenerative-support' && 
                node.attrs.context) {
                
                const context = node.attrs.context;
                
                // Only process each context once
                if (processedContexts.has(context)) return;
                processedContexts.add(context);
                
                const newBlocks = newBlocksByContext[context];
                if (newBlocks && newBlocks.length > 0) {
                    // Find all existing blocks for this context
                    const existingBlocks = [];
                    state.doc.descendants((n, p) => {
                        if (n.type.name === 'paragraph' && 
                            n.attrs.blockType === 'regenerative-support' && 
                            n.attrs.context === context) {
                            existingBlocks.push({ node: n, pos: p });
                        }
                    });
                    
                    if (existingBlocks.length > 0) {
                        // Sort by position (ascending) to process from top to bottom
                        existingBlocks.sort((a, b) => a.pos - b.pos);
                        
                        // Get the position range to replace
                        const firstPos = existingBlocks[0].pos;
                        const lastBlock = existingBlocks[existingBlocks.length - 1];
                        const lastPos = lastBlock.pos + lastBlock.node.nodeSize;
                        
                        // Convert HTML blocks to ProseMirror nodes
                        const nodesToInsert = [];
                        newBlocks.forEach(htmlBlock => {
                            const attrs = {
                                blockType: 'regenerative-support',
                                context: htmlBlock.getAttribute('data-context'),
                                finalContent: htmlBlock.getAttribute('data-final-content'),
                                name: htmlBlock.getAttribute('data-name')
                            };
                            
                            // Handle claim attribute if present
                            const claim = htmlBlock.getAttribute('data-claim');
                            if (claim) {
                                attrs.claim = claim;
                            }
                            
                            const textContent = htmlBlock.textContent;
                            const paragraph = state.schema.nodes.paragraph.create(
                                attrs,
                                textContent ? state.schema.text(textContent) : null
                            );
                            nodesToInsert.push(paragraph);
                        });
                        
                        // Replace all existing blocks with new ones in a single operation
                        // This avoids the visual glitch of empty blocks appearing
                        tr.replaceWith(firstPos, lastPos, nodesToInsert);
                        
                        hasUpdates = true;
                    }
                }
            }
        });
        
        // Apply the transaction if there were updates
        if (hasUpdates) {
            view.dispatch(tr);
        } else {
            // If no updates were made (e.g., no existing regenerative blocks found),
            // fall back to setContent
            editor.commands.setContent(this.regenerativeContent);
        }
    }

    /**
     * Clean up subscriptions
     */
    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }
}
